import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { codexApi } from '../api';
import * as Icons from 'lucide-react';
import { Lock, KeyRound, ChevronRight, Check } from 'lucide-react';

function ChamberIcon({ name, size = 28, color }) {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp size={size} color={color} />;
}

export default function CodexChambersList() {
  const [chambers, setChambers] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    codexApi.listChambers().then(setChambers).catch((e) => setError(e.message));
  }, []);

  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'salmon' }}>{error}</div>;
  if (!chambers) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Opening the vault…</div>;

  return (
    <div data-testid="codex-chambers-list" style={{ paddingTop: 8 }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 22 }}>
        <div className="codex-eyebrow">THE MYSTERY VAULT</div>
        <h1 className="codex-h1" style={{ marginTop: 6 }}>Seven Chambers</h1>
        <p style={{ opacity: 0.75, marginTop: 8, maxWidth: 580 }}>
          Each chamber unlocks as you master more scrolls. Walk in slowly. The mysteries reward those who linger.
        </p>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {chambers.map((c, i) => {
          const pct = c.total_lessons ? Math.round((c.completed_lessons / c.total_lessons) * 100) : 0;
          const allDone = c.completed_lessons === c.total_lessons && c.total_lessons > 0;
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileHover={c.unlocked ? { y: -3 } : undefined}
              data-testid={`codex-chamber-${c.slug}`}
            >
              {c.unlocked ? (
                <Link
                  to={`/codex/chambers/${c.id}`}
                  className={allDone ? 'codex-card-gold codex-card' : 'codex-card'}
                  style={{
                    display: 'block',
                    padding: 22,
                    textDecoration: 'none',
                    color: 'var(--codex-parchment)',
                    borderColor: allDone ? 'rgba(212,175,55,0.6)' : `${c.accent}55`,
                    background: allDone ? undefined : `linear-gradient(180deg, ${c.accent}18, rgba(168,85,247,0.06))`,
                    cursor: 'pointer',
                  }}
                >
                  <ChamberCardInner c={c} pct={pct} unlocked />
                </Link>
              ) : (
                <div
                  className="codex-card"
                  style={{
                    padding: 22,
                    opacity: 0.65,
                    cursor: 'not-allowed',
                  }}
                >
                  <ChamberCardInner c={c} pct={pct} unlocked={false} />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function ChamberCardInner({ c, pct, unlocked }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: unlocked ? `linear-gradient(135deg, ${c.accent}, ${c.accent}aa)` : 'rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: unlocked ? '#1a0e2c' : 'rgba(253,246,227,0.4)',
        }}>
          {unlocked ? <ChamberIcon name={c.icon} /> : <Lock size={22} />}
        </div>
        <div style={{ flex: 1 }}>
          <div className="codex-display" style={{ fontSize: 18, color: 'var(--codex-gold-light)' }}>{c.name}</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
            {c.completed_lessons}/{c.total_lessons} lessons
          </div>
        </div>
        {unlocked ? <ChevronRight size={18} /> : <KeyRound size={16} color="var(--codex-gold-light)" />}
      </div>

      <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.6, minHeight: 36 }}>{c.short_desc}</div>

      {/* Progress bar */}
      <div style={{ marginTop: 14, height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ height: '100%', background: c.accent }}
        />
      </div>

      {!unlocked && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--codex-gold-light)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <KeyRound size={12} /> Master {c.keys_required} word{c.keys_required === 1 ? '' : 's'} to unlock
        </div>
      )}
      {unlocked && pct === 100 && (
        <div style={{ marginTop: 12, fontSize: 12, color: '#7ee2a3', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={14} /> Chamber sealed
        </div>
      )}
    </>
  );
}
